import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import { Search, Plus, ChevronDown, ChevronRight, CalendarPlus, UserPlus, ShoppingCart, Check } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { cn } from "@/lib/cn";
import { UserMenu } from "./UserMenu";
import { SoftphoneChip } from "./SoftphoneChip";
import { NotificationBell } from "./NotificationBell";
import { UniversalSearchDropdown } from "@/features/universal-search/UniversalSearchDropdown";
import { NewDispatchLauncher } from "@/features/board/NewDispatchLauncher";
import { AddCustomerModal } from "@/features/customer-360/AddCustomerModal";
import { CopyCode } from "@/components/Copyable";
import { OFF_RAIL, groupOf, railItemFor, pageLabelFor, type NavGroup } from "./nav";
import { reportTitle } from "@/features/reports/catalog";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { displayCustomerName } from "@/lib/format";

/**
 * Breadcrumb (HD-PASS 2 → CRUMB-NAV-01, 2026-09-02) — the top bar says WHERE
 * you are the way Linear / Notion / GitHub do: `Group › Page › #record`.
 *
 *   · Every segment before the last is a real place you can click: the page
 *     crumb goes to its list (Customers, Board, Invoices…), the group crumb
 *     opens a picker of that group's pages (a group isn't a page, so it can't
 *     "go" anywhere — the GitHub idiom).
 *   · A group shows only when the rail shows its heading (Sell & Build,
 *     Company). The top group has no heading in the rail, so it has no crumb —
 *     the old "The Day" was a name Seth had never seen anywhere else.
 *   · The record id stays a copy chip (COPY-01); uuid routes (jobs, proposals)
 *     show a plain noun instead — a uuid is not a number anyone wants.
 *
 * The page owns its title (PageHeader); this is wayfinding only.
 */
type Crumb = {
  group: NavGroup | null;
  /** The page segment. `to` is set when it's a link (a record/sub-page is open). */
  page: { label: string; to: string | null };
  /** Last segment when a record is open: a copyable code or a plain leaf label. */
  record?: { code: string; label: string; copyLabel: string } | null;
  leaf?: string | null;
};

const RECORD_ROUTES: Record<string, { noun: string; copyLabel: string; hash?: boolean }> = {
  customers: { noun: "Customer", copyLabel: "Copy customer number" },
  dispatch: { noun: "Dispatch", copyLabel: "Copy call number", hash: true },
  invoice: { noun: "Invoice", copyLabel: "Copy invoice number", hash: true },
  jobs: { noun: "Job", copyLabel: "Copy job number" },
  quotes: { noun: "Proposal", copyLabel: "Copy proposal number" },
};

function crumbFor(pathname: string, search: string): Crumb {
  const parts = pathname.split("/").filter(Boolean);
  const root = parts[0] ?? "";
  const rail = railItemFor(root);
  const off = OFF_RAIL[root];
  const group = rail ? rail.group : off ? groupOf(off.group) : null;
  const label = rail?.item.label ?? off?.label ?? "Pegasus";
  const id = parts[1] ?? null;

  // Record routes: the id is a code → the page crumb links to its list and
  // the code becomes the copy chip. A dispatch's list is the Board; an
  // invoice's is Invoices.
  const rec = RECORD_ROUTES[root];
  if (id && rec) {
    const listTo = off?.parent ?? `/${root}`;
    const parentLabel = off?.parent ? pageLabelFor(off.parent.slice(1)) : label;
    const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-/i.test(id);
    // CRUMB-NAV-02 (Seth 2026-10-01, on an invoice opened from a 360: "the
    // breadcrumbs feel off — why does it default to Sell & Build?"): the
    // group segment is rail wayfinding — it earns its keep on LIST pages.
    // With a specific record open it's noise ahead of the answer, so record
    // crumbs read `Invoices › #128132`, never `Sell & Build › Invoices › …`.
    // (Crumbs stay LOCATION, not path — same URL, same crumb for everyone;
    // the Back link is what returns you the way you came.)
    if (isUuid) return { group: null, page: { label: parentLabel, to: listTo }, leaf: rec.noun };
    return {
      group: null,
      page: { label: parentLabel, to: listTo },
      record: { code: id, label: rec.hash ? `#${id.replace(/^0+/, "")}` : id, copyLabel: rec.copyLabel },
    };
  }
  if (root === "agreements" && id) {
    return {
      group: null,
      page: { label, to: "/agreements" },
      leaf: id === "planner" ? "Tune-Up Planner" : id === "route" ? "Route & Batch" : id,
    };
  }
  // Reports: the open report is the leaf (Company › Reports › Revenue & Sales);
  // "Reports" links back to the picker's default.
  if (root === "reports") {
    const title = reportTitle(new URLSearchParams(search).get("report"));
    if (title) return { group, page: { label, to: "/reports" }, leaf: title };
  }
  // Off-rail config pages hang off a rail page (Comms Automations ← Settings).
  if (off?.parent && !rec) {
    return { group: group && group.label ? group : null, page: { label: pageLabelFor(off.parent.slice(1)), to: off.parent }, leaf: label };
  }
  return { group: group && group.label ? group : null, page: { label, to: null } };
}

function pageTitleFor(pathname: string): string {
  return pageLabelFor(pathname.split("/").filter(Boolean)[0] ?? "");
}

/**
 * CRUMB-NAV-03 (Seth 2026-10-09: "the customer number isn't clickable — can
 * we change it to the actual customer name, and take me to their 360?"):
 * a record belongs to a CUSTOMER, so the crumb says so — Invoices › Jane
 * Ehrlich › #117685 — and the name links to the 360 (location, not path:
 * the page's ← Back link is still what returns you the way you came).
 * On /customers/<custno> the copy chip itself wears the name (still copies
 * the number).
 */
function useCrumbCustomer(root: string, id: string | null): { custno: string; name: string } | null {
  const want = !!id && !/^[0-9a-f]{8}-[0-9a-f]{4}-/i.test(id) && (root === "invoice" || root === "dispatch" || root === "customers");
  const { data } = useQuery({
    queryKey: ["crumb_customer", root, id],
    enabled: want,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<{ custno: string; name: string } | null> => {
      let custno: string | null = root === "customers" ? (id as string) : null;
      if (root === "invoice") {
        const { data: s } = await supabase.from("mirror_sales").select("custno").eq("invoice", id as string).maybeSingle();
        custno = (s as { custno?: string } | null)?.custno ?? null;
      } else if (root === "dispatch") {
        const { data: d } = await supabase.from("mirror_dispatch").select("custno").eq("dispatch", id as string).maybeSingle();
        custno = (d as { custno?: string } | null)?.custno ?? null;
      }
      if (!custno) return null;
      const { data: c } = await supabase.from("mirror_customer").select("fullname").eq("custno", custno).maybeSingle();
      const name = displayCustomerName(null, (c as { fullname?: string | null } | null)?.fullname ?? null);
      return { custno, name: name || custno };
    },
  });
  return want ? (data ?? null) : null;
}

const CRUMB_LINK =
  "-mx-1 rounded-md px-1 py-0.5 leading-none text-slate-500 transition-colors duration-150 hover:bg-slate-100 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/30";

/**
 * Group crumb — a picker, not a link. Clicking "Sell & Build" drops the
 * group's pages so a crumb is also the fastest way to a sibling page
 * (GitHub's owner / repo crumbs). Hidden below xl like before: on a narrow
 * bar the search field wins.
 */
function GroupCrumb({ group, current }: { group: NavGroup; current: string }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative hidden shrink-0 xl:block">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        title={`Jump to a ${group.label} page`}
        className={cn("flex items-center gap-0.5", CRUMB_LINK, open && "bg-slate-100 text-slate-900")}
      >
        {group.label}
        <ChevronDown className={cn("h-3 w-3 text-slate-400 transition-transform", open && "rotate-180")} />
      </button>
      {open && (
        <div role="menu" data-glass="panel" className="absolute left-0 z-50 mt-1.5 w-56 overflow-hidden rounded-2xl p-1.5">
          {group.items.map((it) => {
            const active = it.to === current;
            return (
              <Link
                key={it.to}
                to={it.to}
                role="menuitem"
                onClick={() => setOpen(false)}
                className={cn(
                  "flex items-center gap-2.5 rounded-lg px-2.5 py-1.5 text-[13.5px] font-medium transition-colors hover:bg-black/[0.04]",
                  active ? "text-slate-900" : "text-slate-600",
                )}
              >
                <it.icon className={cn("h-4 w-4", active ? "text-brand-700" : "text-slate-400")} strokeWidth={1.9} />
                <span className="flex-1">{it.label}</span>
                {active && <Check className="h-3.5 w-3.5 text-brand-700" strokeWidth={2.5} />}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

// Debounce window for the dropdown query. Matches CustomerSearchView's
// existing pattern so React Query cache entries align between the two.
const DEBOUNCE_MS = 250;

/**
 * SHELL-HD (2026-09-01) — the top bar. Three jobs, left to right: say where
 * you are (big display title), find anything (the search is the bar's hero —
 * wider, softer, keyboard hint as a real key cap), and START something (the
 * "+ New" launcher: dispatch / customer / purchase order from ANY page — the
 * ServiceTitan "+" and Jobber "Create" idiom, so booking a call never
 * requires navigating to the board first).
 */
export function Header({ onOpenPalette }: { onOpenPalette?: () => void }) {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const title = pageTitleFor(pathname);
  const crumb = crumbFor(pathname, search);
  // CRUMB-NAV-03: the customer the open record belongs to (invoice/dispatch),
  // or the customer the open 360 IS — name in the crumb, number in the clipboard.
  const [crumbRoot, crumbId] = pathname.split("/").filter(Boolean);
  const crumbCust = useCrumbCustomer(crumbRoot ?? "", crumbId ?? null);

  const [searchValue, setSearchValue] = useState("");
  const [debouncedValue, setDebouncedValue] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  // -1 means "no row is selected yet". The user must either arrow-down OR
  // mouse-hover into the dropdown to commit a selection. Enter at -1 falls
  // back to the full /customers?q=X search page instead of opening the
  // first row by accident.
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [rowCount, setRowCount] = useState(0);

  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Browser tab title — overridden by deeper routes (CustomerDetailView etc).
  // Keyed on the pathname too, so leaving a record for its list (same page
  // name) resets the tab from "Beth Rollie" back to "Customers".
  useEffect(() => {
    document.title = `Pegasus · ${title}`;
  }, [title, pathname]);

  // Debounce the search query.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedValue(searchValue), DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [searchValue]);

  // Reset selection when the query changes — back to "no selection" so Enter
  // falls back to the full search page until the user explicitly arrows or
  // hovers into a row.
  useEffect(() => {
    setSelectedIndex(-1);
  }, [debouncedValue]);

  // Click outside the wrapper closes the dropdown.
  useEffect(() => {
    if (!isOpen) return;
    function onPointerDown(e: PointerEvent) {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [isOpen]);

  // Dropdown is open when the user has typed enough AND the input has focus
  // OR has had focus since the last close. We use explicit isOpen state
  // (rather than focus alone) so click-on-row can close cleanly.
  function handleInputFocus() {
    if (searchValue.trim().length >= 2) setIsOpen(true);
  }

  function handleSearchKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Escape") {
      setIsOpen(false);
      inputRef.current?.blur();
      return;
    }

    if (e.key === "ArrowDown") {
      if (!isOpen && searchValue.trim().length >= 2) {
        setIsOpen(true);
        return;
      }
      e.preventDefault();
      // From -1 (no selection) ArrowDown jumps to 0 (first row); otherwise
      // increment clamped to last row.
      setSelectedIndex((i) => Math.min(rowCount - 1, i + 1));
      return;
    }

    if (e.key === "ArrowUp") {
      e.preventDefault();
      // Allow ArrowUp to walk back UP to -1 (clears selection, returns
      // Enter to fallback behavior). Some users will find this useful;
      // others won't notice. Clamp at -1 minimum.
      setSelectedIndex((i) => Math.max(-1, i - 1));
      return;
    }

    if (e.key === "Enter") {
      // Only open the selected row when the user has EXPLICITLY selected
      // one (selectedIndex >= 0 means via arrow keys or mouse hover).
      // selectedIndex of -1 means "no selection yet" → fall through to
      // the full search page.
      if (isOpen && rowCount > 0 && selectedIndex >= 0) {
        e.preventDefault();
        window.dispatchEvent(new CustomEvent("pegasus-search-open-selected"));
        return;
      }
      // Fallback: dropdown closed OR no rows OR no explicit selection yet
      // → existing behavior, jump to the customer search page with the
      // query. This is the most common "I just want to search" path.
      const q = searchValue.trim();
      if (q) {
        navigate(`/customers?q=${encodeURIComponent(q)}`);
        setSearchValue("");
        setIsOpen(false);
      }
    }
  }

  function handleInputChange(value: string) {
    setSearchValue(value);
    if (value.trim().length >= 2) {
      setIsOpen(true);
    } else {
      setIsOpen(false);
    }
  }

  // DESKTOP-GLASS-01: the bar FLOATS over the page (absolute; <main> carries
  // pt-16), so the page scrolls up under its frost. The frost is its own
  // layer behind the content (data-glass="bar"): on the <header> itself it
  // would stop the New menu and the search results from frosting the page (a
  // blur inside a blur only sees its parent). `right` stops at main's
  // scrollbar (Layout measures --main-sbw). z-40 keeps every dropdown in here
  // above the page's positioned bits — the board's segmented control once bled
  // through the notification panel (Seth 2026-09-02), and the 360's sticky
  // band (z-30, later in the DOM) sat over the bell's panel (Seth 2026-10-20).
  // Modals are fixed z-50 in portals, so they still win.
  return (
    <header
      data-glass="topbar"
      style={{ right: "var(--main-sbw, 0px)" }}
      className="absolute left-0 top-0 z-40 flex h-16 items-center justify-between gap-4 border-b px-5 xl:px-6"
    >
      <div aria-hidden data-glass="bar" className="pointer-events-none absolute inset-0 -z-10" />
      {/* NOTE: a <span>, not <h1> — index.css gives h1–h3 `text-wrap: balance`
          UNLAYERED, which beats Tailwind's layered `truncate`/`nowrap` on any
          heading (cascade layers, not specificity). The page's PageHeader
          carries the real heading. */}
      <nav aria-label="Breadcrumb" className="flex min-w-0 shrink items-center gap-1.5 whitespace-nowrap text-[13.5px] font-medium">
        {crumb.group && (
          <>
            <GroupCrumb group={crumb.group} current={crumb.page.to ?? `/${pathname.split("/").filter(Boolean)[0] ?? ""}`} />
            <ChevronRight className="hidden h-3.5 w-3.5 shrink-0 text-slate-300 xl:inline" />
          </>
        )}
        {crumb.page.to ? (
          <Link to={crumb.page.to} className={cn("truncate", CRUMB_LINK)} title={`Back to ${crumb.page.label}`}>
            {crumb.page.label}
          </Link>
        ) : (
          <span aria-current="page" className="truncate leading-none font-semibold text-slate-900">{crumb.page.label}</span>
        )}
        {/* CRUMB-NAV-03: the record's customer, by NAME, linking to their 360.
            (Not on /customers — there the name becomes the record chip itself.) */}
        {crumbCust && crumbRoot !== "customers" && (
          <>
            <ChevronRight className="h-3.5 w-3.5 shrink-0 text-slate-300" />
            <Link
              to={`/customers/${crumbCust.custno}`}
              title={`Open ${crumbCust.name}'s 360`}
              className={cn("max-w-[14rem] truncate", CRUMB_LINK)}
            >
              {crumbCust.name}
            </Link>
          </>
        )}
        {(crumb.record || crumb.leaf) && (
          <>
            <ChevronRight className="h-3.5 w-3.5 shrink-0 text-slate-300" />
            {crumb.record ? (
              <CopyCode value={crumb.record.code} label={crumb.record.copyLabel} size="md" className="font-semibold text-slate-900">
                {crumbRoot === "customers" && crumbCust ? crumbCust.name : crumb.record.label}
              </CopyCode>
            ) : (
              <span aria-current="page" className="truncate leading-none font-semibold text-slate-900">{crumb.leaf}</span>
            )}
          </>
        )}
      </nav>
      <div className="flex shrink-0 items-center gap-2.5">
        <div ref={wrapperRef} className="relative">
          <Search className="pointer-events-none absolute left-3.5 top-1/2 z-[1] h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            ref={inputRef}
            type="text"
            value={searchValue}
            onChange={(e) => handleInputChange(e.target.value)}
            onFocus={handleInputFocus}
            onKeyDown={handleSearchKeyDown}
            placeholder="Search customers, addresses, phones, call #…"
            aria-label="Search customers and dispatches"
            aria-autocomplete="list"
            aria-expanded={isOpen}
            aria-controls="universal-search-dropdown"
            data-glass="capsule"
            className="h-10 w-52 rounded-full border pl-10 pr-20 text-[13.5px] lg:w-72 xl:w-[26rem] text-slate-900 placeholder:text-slate-500 transition-all duration-150 ease-out-quart hover:bg-white/85 focus:border-brand-500/60 focus:bg-white focus:outline-none focus:ring-4 focus:ring-brand-500/15"
          />
          {/* Ctrl+K affordance — a real key cap; click works too (DESIGN-SPRINT-01) */}
          <button
            type="button"
            tabIndex={-1}
            onClick={onOpenPalette}
            title="Open the command palette (Ctrl+K)"
            aria-label="Open the command palette"
            className="absolute right-2 top-1/2 flex -translate-y-1/2 items-center gap-0.5 rounded-md border border-hairline bg-white px-1.5 py-0.5 font-mono text-[10.5px] font-semibold text-slate-500 shadow-[0_1px_0_rgba(0,0,0,0.05)] transition-colors hover:text-slate-800"
          >
            Ctrl<span className="text-slate-300">+</span>K
          </button>
          {isOpen && searchValue.trim().length >= 2 && (
            <UniversalSearchDropdown
              query={debouncedValue}
              selectedIndex={selectedIndex}
              onRowCountChange={(newCount) => {
                setRowCount(newCount);
                // Clamp selection: if it's now past the end (results shrank
                // while typing) snap to the last row; if newCount is 0,
                // reset to -1 so Enter falls back to the search page.
                setSelectedIndex((i) => {
                  if (newCount === 0) return -1;
                  if (i >= newCount) return newCount - 1;
                  return i;
                });
              }}
              onHoverIndex={(i) => setSelectedIndex(i)}
              onClose={() => {
                setIsOpen(false);
                setSearchValue("");
              }}
            />
          )}
        </div>
        <NewLauncher />
        {/* DOCK-TIDY-01: the browser phone's on/off switch — header chrome,
            never floating over page content. */}
        <SoftphoneChip />
        <NotificationBell />
        <UserMenu />
      </div>
    </header>
  );
}

/**
 * "+ New" — start the three things a dispatcher starts all day, from any page:
 * a dispatch (customer picker → the normal Create Dispatch modal, today
 * prefilled, tech left open), a customer, a purchase order (opens the PO
 * page with the form already up). Proposals stay where they belong — they're
 * born from a customer or a call, never blank.
 */
function NewLauncher() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [dispatch, setDispatch] = useState(false);
  const [customer, setCustomer] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const items: { label: string; hint: string; icon: React.ComponentType<{ className?: string }>; run: () => void }[] = [
    { label: "Dispatch", hint: "Book a call for a customer", icon: CalendarPlus, run: () => setDispatch(true) },
    { label: "Customer", hint: "Someone new to Sedgwick Heating", icon: UserPlus, run: () => setCustomer(true) },
    { label: "Purchase Order", hint: "Order parts from a vendor", icon: ShoppingCart, run: () => navigate("/purchase-orders?new=1") },
  ];

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        title="Start something new"
        className={cn(
          // DESKTOP-GLASS-01: the one blue accent, lit from above (the phone's primary button).
          "flex h-10 items-center gap-1.5 rounded-full bg-gradient-to-b from-brand-500 to-brand-600 pl-3.5 pr-3 text-[13.5px] font-semibold text-white shadow-[0_8px_20px_-8px_rgb(37_99_235/0.6)] transition-all duration-150 ease-out-quart hover:from-brand-600 hover:to-brand-700 active:scale-[0.98] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-brand-500/25",
          open && "from-brand-600 to-brand-700",
        )}
      >
        <Plus className="h-4 w-4" strokeWidth={2.5} />
        New
        <ChevronDown className={cn("h-3.5 w-3.5 text-white/70 transition-transform", open && "rotate-180")} />
      </button>
      {open && (
        <div role="menu" data-glass="panel" className="absolute right-0 z-50 mt-1.5 w-72 overflow-hidden rounded-2xl p-1.5">
          {items.map((it) => (
            <button
              key={it.label}
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                it.run();
              }}
              className="flex w-full items-center gap-3 rounded-lg px-2.5 py-2 text-left transition-colors hover:bg-black/[0.04]"
            >
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-brand-600/10 text-brand-700">
                <it.icon className="h-4 w-4" />
              </span>
              <span className="min-w-0">
                <span className="block text-[13.5px] font-semibold text-slate-900">{it.label}</span>
                <span className="block text-xs text-slate-500">{it.hint}</span>
              </span>
            </button>
          ))}
        </div>
      )}
      {dispatch && <NewDispatchLauncher empno={null} date={todayISO()} onClose={() => setDispatch(false)} />}
      {customer && <AddCustomerModal onClose={() => setCustomer(false)} />}
    </div>
  );
}
